import { getSignedUrl } from "api/admin";
import { useRef, useState } from "react";
import { MdDownload } from "react-icons/md";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { setLoading } from "store/appSlice";
import GenericCard from "widgets/cards/generic";
import CircleButton from "widgets/controls/buttons/circle-button";
import SelectFile from "widgets/shared/selectFile";
import * as XLSX from "xlsx";
import BulkUploadSvg from "../../../../assets/svg/bulk-upload.svg";
import { SecondaryHeightLayout } from "hooks/viewport-dimensions";
import AuditForm, { AuditFormType } from "widgets/forms/support/audit";
import { toCamelCase } from "util/hooks";
import { dataUsageAudit } from "api/report";
import { DeviceAuditUploadSingleBody } from "api/types/report";
import Combobox from "widgets/shared/inputs/combobox";
import { accountSiteList } from "store/appSelectors";
import { useTranslation } from "react-i18next";

const AuditTools = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [jsonData, setJsonData] = useState(null);
  const [selectedSite, setSelectedSite] = useState<string>("");
  const fileInputRef = useRef(null); // Add a reference to the file input element
  const userInfo = useSelector(accountSiteList, shallowEqual);

  const handleFileUpload = (file: any) => {
    if (file) {
      const reader = new FileReader();

      reader.onload = (e) => {
        const data = new Uint8Array(e.target.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: "array" });

        // Assuming first sheet is the main sheet
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];

        // Convert sheet to JSON
        const parsedData = XLSX.utils.sheet_to_json(sheet);
        const processedData = processParsedData(parsedData);

        setJsonData(processedData);
      };

      reader.readAsArrayBuffer(file);
    }
  };
  const processParsedData = (data: any[]) => {
    // Get all unique keys from the data
    const allKeys = Array.from(
      new Set(data.flatMap((item) => Object.keys(item)))
    );

    return data.map((item) => {
      const newItem: any = {};
      allKeys.forEach((key) => {
        const newKey = toCamelCase(key);
        let newValue = item[key];
        if (typeof newValue === "string") {
          newValue = newValue.replace("(optional)", "").trim();
        }
        newItem[newKey] =
          newValue !== undefined && newValue !== null ? String(newValue) : "";
      });
      return newItem;
    });
  };
  const downloadExcel = async () => {
    dispatch(setLoading(true));
    const { data, isLoading } = await getSignedUrl(
      "meterReadingAuditTemplate.xlsx"
    );
    dispatch(setLoading(isLoading));

    if (data?.signedUrl) {
      const link = document.createElement("a");
      link.href = data?.signedUrl;
      link.target = "_blank";
      link.download = "bulkUpload.xlsx";
      link.click();
    }
  };

  const doBulkUpload = async (form: AuditFormType) => {
    dispatch(setLoading(true));

    const { data, status, isLoading } = await dataUsageAudit({
      siteId: selectedSite,
      type: "WATER",
      devices: form.devices as DeviceAuditUploadSingleBody[],
    });
    dispatch(setLoading(isLoading));

    if (status && status === 200) {
      if (data && data.url && data.url.excelUrl) {
        downloadGeneratedExcel(data.url.excelUrl);
      }
    }
  };

  // Download Excel file
  const downloadGeneratedExcel = (url: string) => {
    const link = document.createElement("a");
    link.href = url;
    link.target = "_blank";
    link.download = "report.xlsx";
    link.click();
  };

  const clearData = () => {
    if (fileInputRef.current) {
      fileInputRef.current.clear(); // Use the clear method to reset the file input element's value
    }
  };

  return (
    <SecondaryHeightLayout>
      <GenericCard
        extra="h-full"
        title={t("support.audit.tools.meter.readings")}
        topRight={
          <>
            <CircleButton
              label={t("button.action.download.template")}
              icon={MdDownload}
              onClick={() => {
                downloadExcel();
              }}
              size="md"
              variant="outline"
              color="secondary"
            />
          </>
        }
      >
        <div className="flex flex-col gap-6">
          <SelectFile
            id={"bulkuploadxlsx"}
            onChange={handleFileUpload}
            sizes="sm"
            ref={fileInputRef}
            color="secondary"
          />
          <div className="flex-1 lg:flex-grow-[1]">
            <Combobox
              filter
              id="device-select-site"
              placeholder={t("support.audit.tools.select.site")}
              variant="solid"
              value={selectedSite}
              onChange={(item) => {
                setSelectedSite(item);
              }}
              items={userInfo}
              labelKey="name"
              valueKey="id"
              sizes="md"
              rounded="rounded-xl"
              color="secondary"
            />
          </div>
          {jsonData && jsonData.length > 0 ? (
            <>
              <AuditForm
                submit={doBulkUpload}
                clearData={clearData}
                onClose={function (): void {}}
                values={{
                  devices: jsonData,
                }}
              />
            </>
          ) : (
            <div className="mt-12 flex justify-center">
              <img
                src={BulkUploadSvg}
                alt="Modal content"
                style={{ width: "20%" }}
              />
            </div>
          )}
        </div>
      </GenericCard>
    </SecondaryHeightLayout>
  );
};

export default AuditTools;
